import { useCallback, useEffect, useRef } from "react";

import FocusLock from "react-focus-lock";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import Form, { FormFooter, FormSection } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import VisuallyHidden from "@atlaskit/visually-hidden";

import {
    FormDatePicker,
    FormSelect,
    FormTextArea,
    FormTextField,
    Option,
    SpotlightTarget
} from "../common";
import { SpotlightManager as Spotlight } from "../common";
import {
    jswInteractiveBoardAnalyticsPayload,
    TRACKING_EVENT_NAMES
} from "../constants/analytics";
import { JiraIssue } from "../constants/types";
import {
    Assignee,
    EpicName,
    FIRST_ISSUE_ID,
    IssueTypeName,
    MAX_ISSUE_SUMMARY_LENGTH,
    Priority,
    Project,
    SUCCESS_MODAL_DELAY,
    Steps
} from "../constants/ui";
import { getSpotlightMessagesForStep } from "../helpers";
import {
    getAssetOptionsForAssignee,
    getAssetOptionsForEpicLink,
    getAssetOptionsForIssueType,
    getAssetOptionsForPriority,
    getAssetOptionsForProject
} from "../helpers";
import { useInteractiveBoardStore, useMessages } from "../state";

import { CloseModalButton } from "./close-modal-button";
import {
    divider,
    formRow,
    modalContainer,
    modalHeader
} from "./create-issue-modal.styles";

export const CreateIssueModal = ({ dueDate }: CreateIssueModalProps) => {
    const [messages] = useMessages();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [
        { step, isDemoComplete },
        {
            resetIncompleteDemo,
            nextDemoStep,
            toggleTicketModalOpen,
            createIssue,
            toggleCompleteDemoModalOpen
        }
    ] = useInteractiveBoardStore();
    const spotlightMessages = getSpotlightMessagesForStep(step, messages);

    const fireAnalyticsEvent = useCallback(
        (eventType: "ui" | "track", trackEvent: TRACKING_EVENT_NAMES) => {
            createAnalyticsEvent({
                ...jswInteractiveBoardAnalyticsPayload,
                eventType,
                attributes: {
                    eventName: trackEvent,
                    demoStep: step + 1 + "/7",
                    isDemoComplete
                }
            }).fire();
        },
        [isDemoComplete, step, createAnalyticsEvent]
    );

    const summaryInputRef = useRef<HTMLInputElement>(null);
    const descriptionTextAreaRef = useRef<HTMLTextAreaElement>(null);
    const prioritySelectRef = useRef<HTMLSelectElement>(null);
    const epicLinkSelectRef = useRef<HTMLSelectElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const closeModalButton = useRef<HTMLButtonElement>(null);

    const onIncrementStep = (
        nextElement?: React.RefObject<
            | HTMLButtonElement
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
            | null
        >
    ) => {
        // disable focus on close modal button while incrementing step
        if (closeModalButton && closeModalButton.current) {
            closeModalButton.current.disabled = true;
        }

        void nextDemoStep();

        // runs after current callstack is cleared i.e. nextDemoStep action.
        setTimeout(() => {
            if (nextElement && nextElement.current) {
                nextElement.current.focus();
            }

            if (closeModalButton && closeModalButton.current) {
                closeModalButton.current.disabled = false;
            }
        }, 50);
    };

    const optionsForProject = [
        {
            label: messages.createIssueModalProjectOption1,
            value: Project.TeamRocket
        }
    ];

    const optionsForIssueType = [
        {
            label: messages.createIssueModalIssueTypeOption1,
            value: IssueTypeName.TASK
        },
        {
            label: messages.createIssueModalIssueTypeOption2,
            value: IssueTypeName.STORY
        },
        {
            label: messages.createIssueModalIssueTypeOption3,
            value: IssueTypeName.BUG
        }
    ];

    const optionsForPriority = [
        {
            label: messages.createIssueModalPriorityOption1,
            value: Priority.HIGH
        },
        {
            label: messages.createIssueModalPriorityOption2,
            value: Priority.CRITICAL
        },
        {
            label: messages.createIssueModalPriorityOption3,
            value: Priority.BLOCKER
        },
        {
            label: messages.createIssueModalPriorityOption4,
            value: Priority.MINOR
        }
    ];

    const optionsForEpicLink = [
        {
            label: messages.createIssueModalEpicLinkOption1,
            value: EpicName.FEATURES,
            description: messages.createIssueModalEpicLinkOption1Description
        },
        {
            label: messages.createIssueModalEpicLinkOption2,
            value: EpicName.ANALYTICS,
            description: messages.createIssueModalEpicLinkOption2Description
        },
        {
            label: messages.createIssueModalEpicLinkOption3,
            value: EpicName.PAYMENTS,
            description: messages.createIssueModalEpicLinkOption3Description
        }
    ];

    const optionsForAssignee = [
        {
            label: messages.createIssueModalAssigneeOption1,
            value: Assignee.ALVIN
        },
        {
            label: messages.createIssueModalAssigneeOption2,
            value: Assignee.JANE
        },
        {
            label: messages.createIssueModalAssigneeOption3,
            value: Assignee.BLAKE
        }
    ];

    const onCloseModal = () => {
        void toggleTicketModalOpen();

        if (!isDemoComplete) {
            void resetIncompleteDemo();
        }
    };

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onCloseModal();
            }
        };

        document.addEventListener("keydown", handleKeyPress);

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, []);

    return (
        <FocusLock>
            <div
                aria-labelledby="createIssueModalHeading"
                aria-modal="true"
                css={modalContainer}
                data-testid="create-issue-modal"
                role="dialog"
            >
                {step > Steps.CREATE_FIRST_STORY && step < Steps.PULSE_CARD && (
                    <Spotlight />
                )}
                <VisuallyHidden role="status">
                    <span>{spotlightMessages.heading}</span>
                    <span>{spotlightMessages.description}</span>
                </VisuallyHidden>
                <div css={modalHeader}>
                    <Heading as="h2" level="h600" id="createIssueModalHeading">
                        {messages.createIssueModalHeader}
                    </Heading>
                    <CloseModalButton
                        forwardRef={closeModalButton}
                        onCloseClick={() => {
                            fireAnalyticsEvent(
                                "ui",
                                TRACKING_EVENT_NAMES.jswInteractiveBoardCreateModalClosed
                            );

                            onCloseModal();
                        }}
                    />
                </div>
                <Form<IssueFormState>
                    onSubmit={(formState: IssueFormState) => {
                        const newJiraIssue: JiraIssue = {
                            id: !isDemoComplete
                                ? FIRST_ISSUE_ID
                                : new Date().toISOString(),
                            summary: formState.summary,
                            issueType: formState.issueType
                                .value as IssueTypeName,
                            epicName: formState.epicLink.value as EpicName,
                            assignee: formState.assignee.value as Assignee,
                            priority: formState.priority.value as Priority,
                            issueDescription: formState.issueDescription,
                            dueDate: new Date(formState.dueDate)
                        };

                        fireAnalyticsEvent(
                            "ui",
                            TRACKING_EVENT_NAMES.jswInteractiveBoardCreateIssue
                        );

                        void createIssue(newJiraIssue);
                        void toggleTicketModalOpen();
                        // Demo-specific logic
                        if (!isDemoComplete) {
                            void nextDemoStep();

                            // Popup success modal after 3 seconds
                            setTimeout(() => {
                                void toggleCompleteDemoModalOpen();
                            }, SUCCESS_MODAL_DELAY);
                        }
                    }}
                >
                    {({ formProps }) => (
                        <form {...formProps}>
                            <FormSection>
                                <div css={formRow}>
                                    <FormSelect
                                        id="textfield-project"
                                        name="project"
                                        label={
                                            messages.createIssueModalInput1Label
                                        }
                                        disabled={
                                            optionsForProject.length === 1
                                        }
                                        required
                                        options={optionsForProject}
                                        defaultValue={optionsForProject[0]}
                                        assetResolver={
                                            getAssetOptionsForProject
                                        }
                                    />
                                    <FormSelect
                                        id="select-issue-type"
                                        data-testid="select-issue-type"
                                        name="issueType"
                                        label={
                                            messages.createIssueModalInput2Label
                                        }
                                        required
                                        options={optionsForIssueType}
                                        defaultValue={optionsForIssueType[0]}
                                        assetResolver={
                                            getAssetOptionsForIssueType
                                        }
                                        step={Steps.SELECT_ISSUE_TYPE}
                                        // eslint-disable-next-line jsx-a11y/no-autofocus
                                        autoFocus //autoFocus is ok to use on modal
                                        onStepComplete={() => {
                                            if (
                                                step === Steps.SELECT_ISSUE_TYPE
                                            ) {
                                                fireAnalyticsEvent(
                                                    "track",
                                                    TRACKING_EVENT_NAMES.jswInteractiveBoardSelectIssueType
                                                );

                                                onIncrementStep(
                                                    summaryInputRef
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                <hr aria-hidden css={divider} />
                                <div css={formRow}>
                                    <FormTextField
                                        id="textfield-summary"
                                        name="summary"
                                        label={
                                            messages.createIssueModalInput3Label
                                        }
                                        required
                                        defaultValue=""
                                        disabled={step < Steps.ADD_SUMMARY}
                                        placeholder={
                                            messages.createIssueModalInput3Placeholder
                                        }
                                        forwardRef={summaryInputRef}
                                        onStepComplete={() => {
                                            if (step === Steps.ADD_SUMMARY) {
                                                fireAnalyticsEvent(
                                                    "track",
                                                    TRACKING_EVENT_NAMES.jswInteractiveBoardAddSummary
                                                );

                                                onIncrementStep(
                                                    descriptionTextAreaRef
                                                );
                                            }
                                        }}
                                        maxCharacterLength={
                                            MAX_ISSUE_SUMMARY_LENGTH
                                        }
                                    />
                                </div>
                                <div css={formRow}>
                                    <FormTextArea
                                        id="textarea-issue-description"
                                        name="issueDescription"
                                        label={
                                            messages.createIssueModalInput4Label
                                        }
                                        required={false}
                                        disabled={step < Steps.ADD_DESCRIPTION}
                                        maxHeight="140"
                                        placeholder={
                                            messages.createIssueModalInput4Placeholder
                                        }
                                        forwardRef={descriptionTextAreaRef}
                                        onStepComplete={() => {
                                            if (
                                                step === Steps.ADD_DESCRIPTION
                                            ) {
                                                fireAnalyticsEvent(
                                                    "track",
                                                    TRACKING_EVENT_NAMES.jswInteractiveBoardAddDescription
                                                );

                                                onIncrementStep(
                                                    prioritySelectRef
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                <div css={formRow}>
                                    <FormSelect
                                        id="select-priority"
                                        name="priority"
                                        label={
                                            messages.createIssueModalInput5Label
                                        }
                                        forwardRef={prioritySelectRef}
                                        required
                                        disabled={step < Steps.SELECT_PRIORITY} // TODO: Set disabled value to true if currentStep < this step.
                                        options={optionsForPriority}
                                        step={Steps.SELECT_PRIORITY}
                                        defaultValue={optionsForPriority[0]}
                                        assetResolver={
                                            getAssetOptionsForPriority
                                        }
                                        onStepComplete={() => {
                                            if (
                                                step === Steps.SELECT_PRIORITY
                                            ) {
                                                fireAnalyticsEvent(
                                                    "track",
                                                    TRACKING_EVENT_NAMES.jswInteractiveBoardSelectPriority
                                                );

                                                onIncrementStep(
                                                    epicLinkSelectRef
                                                );
                                            }
                                        }}
                                    />
                                    <FormSelect
                                        id="select-epic-link"
                                        name="epicLink"
                                        label={
                                            messages.createIssueModalInput6Label
                                        }
                                        forwardRef={epicLinkSelectRef}
                                        required
                                        disabled={step < Steps.SELECT_EPIC_LINK} // TODO: Set disabled value to true if currentStep < this step.
                                        options={optionsForEpicLink}
                                        step={Steps.SELECT_EPIC_LINK}
                                        defaultValue={optionsForEpicLink[0]}
                                        assetResolver={
                                            getAssetOptionsForEpicLink
                                        }
                                        simplifiedView
                                        onStepComplete={() => {
                                            if (
                                                step === Steps.SELECT_EPIC_LINK
                                            ) {
                                                fireAnalyticsEvent(
                                                    "track",
                                                    TRACKING_EVENT_NAMES.jswInteractiveBoardSelectEpicLink
                                                );

                                                onIncrementStep(
                                                    submitButtonRef
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                <div css={formRow}>
                                    <FormSelect
                                        id="select-assignee"
                                        name="assignee"
                                        label={
                                            messages.createIssueModalInput7Label
                                        }
                                        required
                                        options={optionsForAssignee}
                                        defaultValue={optionsForAssignee[0]}
                                        disabled={!isDemoComplete}
                                        assetResolver={
                                            getAssetOptionsForAssignee
                                        }
                                    />
                                    <FormDatePicker
                                        id="select-due-date"
                                        name="dueDate"
                                        label={
                                            messages.createIssueModalInput8Label
                                        }
                                        required
                                        disabled={!isDemoComplete}
                                        defaultValue={dueDate}
                                    />
                                </div>
                            </FormSection>
                            <hr aria-hidden css={divider} />
                            <FormFooter>
                                <SpotlightTarget name="create">
                                    <Button
                                        id="modal-submit-btn"
                                        appearance="primary"
                                        type="submit"
                                        ref={submitButtonRef}
                                        isDisabled={
                                            !isDemoComplete &&
                                            step != Steps.MOVE_ISSUE
                                        }
                                    >
                                        {messages.createIssueModalSubmitBtn}
                                    </Button>
                                </SpotlightTarget>
                            </FormFooter>
                        </form>
                    )}
                </Form>
            </div>
        </FocusLock>
    );
};

type IssueFormState = {
    dueDate: string;
    assignee: Option;
    epicLink: Option;
    priority: Option;
    issueDescription: string;
    summary: string;
    issueType: Option;
    project: Option;
};

type CreateIssueModalProps = {
    dueDate?: string;
    locale?: string;
    timeZone?: string;
};
